import { IonCol, IonGrid, IonRow, IonSpinner } from '@ionic/react';
import { ApexOptions } from 'apexcharts';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
// eslint-disable-next-line import/default
import Chart from 'react-apexcharts';

import styles from '@/pages/ReportV2/components/Graphs/ApexCharts.module.css';

import { InputSwitch } from '@/components/InputSwitch';
import { ReportModal } from '@/components/Modal/ReportModal';

import { KinematicYAxis, kinematicGraphAnnotations } from '@/models/reportV2.model';

type KinematicsGraphV2Props = {
  data: any;
  subtitle: string;
  graphAnnotations: kinematicGraphAnnotations;
  strokeWidth: number;
  forPdf?: boolean;
  yAxis: KinematicYAxis;
  title?: string;
  modalData?: any; // Data to show in the modal for toggling strides
  parentShowAllStrides?: boolean; // Parent component's showAllStrides state
};

// eslint-disable-next-line react/display-name
export const KinematicsGraphV2 = React.forwardRef(
  (
    {
      data,
      subtitle,
      graphAnnotations,
      strokeWidth,
      forPdf = false,
      yAxis,
      title,
      modalData,
      parentShowAllStrides,
    }: KinematicsGraphV2Props,
    ref: React.ForwardedRef<Chart>,
  ) => {
    const [loading, setLoading] = useState(!forPdf ? true : false);
    const [showModal, setShowModal] = useState({ show: false });
    const [showLegend, setShowLegend] = useState(false);
    const [showAllStrides, setShowAllStrides] = useState<boolean>(parentShowAllStrides ?? false);

    // extract the plane from the title for the data filtering for the modal
    // Title is constructed as 'Segment - Plane', e.g., 'Pelvis - Sagittal'
    // Map 'Frontal' to 'Coronal' as the data uses 'Coronal' naming
    const plane = useMemo(() => {
      if (title) {
        const parts = title.split(' - ');
        if (parts.length === 2) {
          const planeName = parts[1];
          // Map Frontal to Coronal for data structure compatibility
          return planeName === 'Frontal' ? 'Coronal' : planeName;
        }
      }
      return null;
    }, [title]);

    // Filter the modal data based on the selected plane (We should have the modalData.allStrides.plane.series or modalData.mean.plane.series)
    const modalDataSeries = useMemo(() => {
      if (modalData && plane) {
        if (showAllStrides) {
          return modalData.allStrides[plane]?.series || [];
        }
        return modalData.mean[plane]?.series || [];
      }
      return [];
    }, [modalData, plane, showAllStrides]);

    // Sync local state with parent state when it changes
    useEffect(() => {
      if (parentShowAllStrides !== undefined) {
        setShowAllStrides(parentShowAllStrides);
      }
    }, [parentShowAllStrides]);

    const toggleShowAllStrides = () => {
      setShowAllStrides((prev) => !prev);
    };

    const options: ApexOptions = {
      chart: {
        type: 'line',
        width: '100%',
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        animations: {
          enabled: false,
        },
        offsetX: -15,
      },
      xaxis: {
        type: 'numeric',
        labels: {
          show: false,
          style: {
            fontSize: '8px',
          },
        },
        axisTicks: {
          show: false,
        },
        // title: {
        //   text: 'Percentage of stride time',
        //   style: {
        //     fontSize: '10px',
        //     fontFamily: 'Montserrat',
        //     fontWeight: 300,
        //   },
        //   offsetY: 5,
        // },
      },
      yaxis: {
        show: true,
        min: yAxis.yMin,
        max: yAxis.yMax,
        tickAmount: yAxis.tickAmount,
        labels: {
          formatter: function (value: number) {
            return value.toFixed(0);
          },
          style: {
            fontSize: '8px',
          },
        },
        axisTicks: {
          show: true,
        },
      },
      grid: {
        show: true,
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      subtitle: {
        text: subtitle,
        align: 'center',
        margin: 0,
        floating: true,
        offsetY: 0,

        style: {
          fontSize: '8px',
          fontFamily: 'Montserrat',
        },
      },
      stroke: {
        width: strokeWidth,
        dashArray: [0],
      },
      legend: {
        show: showLegend,
        position: 'bottom',
        horizontalAlign: 'center',
        offsetY: 5,
        fontSize: '9px',
        markers: {
          width: 8,
          height: 8,
        },
        onItemClick: {
          toggleDataSeries: true,
        },
      },
      annotations: {
        xaxis: graphAnnotations,
        yaxis: [
          {
            y: 0,
            borderColor: 'red',
          },
        ],
      },
      markers: {
        size: [0, 0, 2],
        strokeWidth: 0,
        showNullDataPoints: false,
      },
      tooltip: {
        enabled: false,
      },
    };

    useEffect(() => {
      if (!forPdf) {
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      }
    }, [data]);

    return (
      <>
        {!forPdf && title && (
          <ReportModal
            header={`${title} Movement`}
            isOpen={showModal.show}
            onDidDismiss={() => {
              setShowModal({ show: false });
              setShowLegend(false);
            }}
            setShowInfo={() => setShowModal({ show: false })}>
            <IonGrid className={styles.modalGrid}>
              <IonRow style={{ width: '100%' }}>
                <IonCol size='12'>
                  <Chart height='250%' options={options} ref={ref} series={modalDataSeries} type='line' />
                </IonCol>
              </IonRow>
              <IonRow className='ion-text-end ion-justify-content-end ion-padding' style={{ width: '100%' }}>
                <InputSwitch
                  checked={showAllStrides}
                  label='Toggle all strides'
                  onIonChange={() => toggleShowAllStrides()}
                />
              </IonRow>
            </IonGrid>
          </ReportModal>
        )}

        <div
          className={styles.kinematicsGraph_Wrapper}
          onClick={() => {
            setShowModal({ show: true });
            setTimeout(() => {
              setShowLegend(true);
            }, 200);
          }}>
          {!loading ? (
            <Chart options={options} ref={ref} series={data} type='line' />
          ) : (
            <IonSpinner color='primary' name='bubbles' />
          )}
        </div>
      </>
    );
  },
);
